import React from "react";
import { createInertiaApp } from "@inertiajs/react";
import { createRoot } from "react-dom/client";
import Honeybadger from "@honeybadger-io/js";
import { HoneybadgerErrorBoundary } from "@honeybadger-io/react";
import CustomErrorBoundaryComponent from "./custom_error_boundary";
import "../utils/axios_setup";
import { initMobiLoudPush } from "../mobiloud";
import { initializeMultiAccount } from "../utils/multi_account_init";
import { initAppNotificationUrlParamHook } from "../utils/app_notification_url_param";

document.addEventListener("DOMContentLoaded", () => {
  // Initialize MobiLoud push notifications (no-op if not in MobiLoud Canvas wrapper)
  initMobiLoudPush();

  // Initialize multi-account support (adds current session to localStorage if enabled)
  initializeMultiAccount();

  // Suppress benign ResizeObserver errors from browser console
  // These occur frequently with Material UI and don't indicate real problems
  window.addEventListener("error", (event) => {
    if (event.message?.includes("ResizeObserver loop")) {
      event.stopImmediatePropagation();
    }
  });

  const honeybadgerEnvironment = window.aws_env?.trim() || window.environment;

  const honeybadger = Honeybadger.configure({
    apiKey: "bcc95794",
    environment: honeybadgerEnvironment,
    // Uncomment to enable reporting in development
    // reportData: true,
  });

  if (window._tkcCurrentUserId) {
    honeybadger.setContext({
      user_id: window._tkcCurrentUserId,
      user_email: window.bloc_user_email,
    });
  }

  const ignorePatterns = [
    /This video will be available for viewing shortly/i,
    /ResizeObserver loop limit exceeded/i,
    /ResizeObserver loop completed with undelivered notifications/i,
  ];

  Honeybadger.beforeNotify(function (notice) {
    if (ignorePatterns.some((p) => p.test(notice.message))) {
      return false;
    }
  });

  const el = document.querySelector("div#app");

  if (el) {
    createInertiaApp({
      resolve: async (name) => {
        const module = await import(`../pages/${name}`);
        return module.default;
      },
      setup({ el, App, props }) {
        createRoot(el).render(
          <HoneybadgerErrorBoundary
            honeybadger={honeybadger}
            ErrorComponent={CustomErrorBoundaryComponent}
          >
            <App {...props} />
          </HoneybadgerErrorBoundary>,
        );
      },
    });

    // notifications_v2 push-tap → mark-read sync (PR-N1). Reads
    // ?app_notification_id=N from the URL on full page load AND on every
    // Inertia client-side navigation, fires PATCH mark_as_read, strips the
    // param so a back-nav doesn't re-fire. Half B (push dismiss) ships in
    // a later PR.
    initAppNotificationUrlParamHook();
  }
});
